'use client';

import React, { useRef, useState } from 'react';
import { PlayCircle } from 'lucide-react';
import { getFileStreamUrl } from '@eliadmin/core/src/api/video/video-api';
import { cn } from '@eliadmin/core/src/utils/style-utils';

export interface OfferVideoProps {
  /**
   * The ID of the video file to stream. If falsy, no video is rendered.
   */
  videoId?: string | null;
  poster?: string | null;
  /**
   * Optional CSS class for styling the container.
   */
  className?: string;
}

export const OfferVideo: React.FC<OfferVideoProps> = ({ videoId, poster, className }) => {
  const videoSrc = videoId ? getFileStreamUrl(videoId) : null;
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const handlePlay = () => setIsPlaying(true);
  const handlePause = () => setIsPlaying(false);

  const togglePlayback = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    if (videoRef.current.paused) void videoRef.current.play().catch(() => {});
    else videoRef.current.pause();
  };

  if (!videoSrc) return null;

  return (
    <div className={cn('relative flex w-full justify-center overflow-hidden bg-black', className)}>
      <video
        ref={videoRef}
        src={videoSrc}
        poster={poster || '/images/placeholder.png'}
        preload="none"
        playsInline
        className="h-auto max-h-[100svh] max-w-full object-contain md:max-h-[70vh]"
        onPlay={handlePlay}
        onPause={handlePause}
        controls
      />

      {!isPlaying && (
        <button
          className="absolute inset-0 flex items-center justify-center"
          onClick={togglePlayback}
          aria-label="Play video"
        >
          <PlayCircle className="h-16 w-16 text-white opacity-75 transition hover:opacity-100" />
        </button>
      )}
    </div>
  );
};
